<template>
  <BasePageCard>
    <div class="freight-tables-view">
      <div class="freight-tables-view__header">
        <h2 class="freight-tables-view__title">Tabela de frete</h2>

        <div class="freight-tables-view__header-actions">
          <FreightTableActionsMenu
            v-if="canImport"
            :items="importActions"
            :loading="importing || downloadingTemplate"
            @select="onHeaderAction"
          />

          <a-button
            v-if="canCreate && !showEmptyState"
            type="primary"
            class="freight-tables-view__create-btn"
            @click="goToCreate"
          >
            <a-icon type="plus" /> Nova tabela
          </a-button>
        </div>
      </div>

      <FreightTablesFilters
        v-if="!showEmptyState"
        :dt-inicio="activeFilters.dtInicio"
        :dt-fim="activeFilters.dtFim"
        :search="activeFilters.search"
        @filter-change="onFilterChange"
        @open-filters="filtersModalVisible = true"
        @period-change="onPeriodChange"
      >
        <template #tabs>
          <BaseSegmentedTabs
            :tabs="statusTabs"
            :active-key="activeTab"
            @change="onStatusTabChange"
          />
        </template>
      </FreightTablesFilters>

      <FreightTablesFilterModal
        :visible="filtersModalVisible"
        :filters="activeFilters"
        @close="filtersModalVisible = false"
        @apply="onApplyFilters"
        @clear="onClearFilters"
      />

      <FreightTableImportErrorsModal
        :visible="importErrorsVisible"
        :error="importError"
        @close="closeImportErrors"
        @retry="onRetryImport"
        @download-report="onDownloadImportReport"
        @download-template="onDownloadSpreadsheet"
      />

      <FreightTableExportModal
        :visible="exportModal.visible"
        :loading="exportModal.loading"
        @close="closeExportModal"
        @confirm="onConfirmExport"
      />

      <BaseBulkActionsBar
        v-if="!showEmptyState && !showNoResultsState && canSelect"
        :selected-count="selectedRowKeys.length"
        :total-count="items.length"
        :actions="bulkActions"
        @toggle-all="onToggleAll"
        @action="onBulkAction"
      />

      <FreightTablesEmptyState
        v-if="showEmptyState"
        :can-create="canCreate"
        @create="goToCreate"
      />

      <a-empty
        v-else-if="showNoResultsState"
        description="Nenhuma tabela de frete encontrada para esse filtro."
        class="freight-tables-view__no-results"
      />

      <FreightTablesTable
        v-else
        :items="items"
        :loading="loading"
        :selected-row-keys="selectedRowKeys"
        :pagination="pagination"
        :selectable="canSelect"
        :can-add-route="canAddRoute"
        :can-add-carrier="canAddCarrier"
        :can-approve="canApprove"
        :can-cancel="canCancel"
        :can-export="canExport"
        @selection-change="selectedRowKeys = $event"
        @view-details="onViewDetails"
        @action="onRowAction"
        @table-change="onTableChange"
      />
    </div>
  </BasePageCard>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { Modal } from "ant-design-vue";
import BasePageCard from "@/shared/components/BasePageCard.vue";
import FreightTablesFilters from "@/modules/FreightTables/components/List/FreightTablesFilters.vue";
import FreightTablesTable from "@/modules/FreightTables/components/List/FreightTablesTable.vue";
import FreightTablesEmptyState from "@/modules/FreightTables/components/List/FreightTablesEmptyState.vue";
import FreightTablesFilterModal from "@/modules/FreightTables/components/List/FreightTablesFilterModal.vue";
import BaseSegmentedTabs from "@/shared/components/BaseSegmentedTabs.vue";
import BaseBulkActionsBar from "@/shared/components/BaseBulkActionsBar.vue";
import FreightTableExportModal from "@/modules/FreightTables/components/FreightTableExportModal.vue";
import FreightTableActionsMenu from "@/modules/FreightTables/components/FreightTableActionsMenu.vue";
import FreightTableImportErrorsModal from "@/modules/FreightTables/components/List/FreightTableImportErrorsModal.vue";
import { confirmDeleteDraft } from "@/modules/FreightTables/utils/confirmDeleteDraft";
import { normalizeImportError } from "@/modules/FreightTables/utils/normalizeImportError";
import { buildImportErrorReport } from "@/modules/FreightTables/utils/buildImportErrorReport";
import { handleApiError } from "@/shared/utils/handleApiError";
import { downloadBlobFile } from "@/shared/utils/downloadBlobFile";
import {
  FREIGHT_TABLE_TABS,
  FREIGHT_TABLE_STATUS_FILTER,
} from "../constants/freightTableStatus";
import { FREIGHT_VALIDITY_STATUS_FILTER } from "../constants/freightValidityStatus";
import {
  readStatusFiltersFromQuery,
  buildStatusFiltersQuery,
  isSameStatusQuery,
} from "../utils/statusFiltersQuery";
import {
  isFreightTablePath,
  addCarrierPath,
  addRoutePath,
} from "../constants/freightTableRoutes";

// Mesmo teto do FreightTableRouteImportRequest no backend.
const IMPORT_MAX_BYTES = 5 * 1024 * 1024;

const IMPORT_ACTIONS = [
  {
    key: "download-template",
    label: "Baixar template",
    icon: "vertical-align-bottom",
  },
  {
    key: "import-spreadsheet",
    label: "Importar planilha",
    icon: "vertical-align-top",
  },
];

export default {
  name: "FreightTablesIndexView",
  components: {
    BasePageCard,
    FreightTablesFilters,
    FreightTablesTable,
    FreightTablesEmptyState,
    FreightTablesFilterModal,
    BaseSegmentedTabs,
    BaseBulkActionsBar,
    FreightTableExportModal,
    FreightTableImportErrorsModal,
    FreightTableActionsMenu,
  },
  data() {
    return {
      selectedRowKeys: [],
      bulkActionPending: null,
      hasFetched: false,
      importActions: IMPORT_ACTIONS,
      importing: false,
      downloadingTemplate: false,
      importErrorsVisible: false,
      importError: null,
      filtersModalVisible: false,
      exportModal: {
        visible: false,
        loading: false,
        freightTableId: null,
        codigo: null,
      },
      activeFilters: {
        search: "",
        sort: null,
        tableStatus: FREIGHT_TABLE_STATUS_FILTER.ALL,
        effectivePeriodStatus: FREIGHT_VALIDITY_STATUS_FILTER.ALL,
        isDraft: null,
        fornecedor: [],
        dtInicio: null,
        dtFim: null,
      },
    };
  },
  computed: {
    ...mapGetters("freightTables", [
      "items",
      "loading",
      "pagination",
      "counts",
      "filters",
    ]),
    statusTabs() {
      return FREIGHT_TABLE_TABS.map((tab) => ({
        key: tab.key,
        label: tab.label,
        count: this.counts[tab.key] || 0,
      }));
    },
    activeTab() {
      const match = FREIGHT_TABLE_TABS.filter(
        (tab) =>
          tab.tableStatus === this.activeFilters.tableStatus &&
          tab.isDraft === this.activeFilters.isDraft,
      )[0];

      return match ? match.key : "todos";
    },
    hasActiveFilter() {
      return (
        !!this.activeFilters.search ||
        this.activeFilters.tableStatus !== FREIGHT_TABLE_STATUS_FILTER.ALL ||
        this.activeFilters.effectivePeriodStatus !==
          FREIGHT_VALIDITY_STATUS_FILTER.ALL ||
        this.activeFilters.isDraft !== null ||
        this.activeFilters.fornecedor.length > 0 ||
        !!this.activeFilters.dtInicio
      );
    },
    noRecords() {
      return this.hasFetched && !this.loading && this.pagination.total === 0;
    },
    showEmptyState() {
      return this.noRecords && !this.hasActiveFilter;
    },
    showNoResultsState() {
      return this.noRecords && this.hasActiveFilter;
    },
    canCreate() {
      return this.getPermissionFw("tabelafrete.index");
    },
    canAddRoute() {
      return this.getPermissionFw("tabelafrete.rota.store");
    },
    canAddCarrier() {
      return this.getPermissionFw("tabelafrete.transportador.store");
    },
    canApprove() {
      return this.getPermissionFw("tabelafrete.aprovar");
    },
    canCancel() {
      return this.getPermissionFw("tabelafrete.cancelar");
    },
    canExport() {
      return this.getPermissionFw("tabelafrete.exportar");
    },
    canImport() {
      return this.getPermissionFw("tabelafrete.importar");
    },
    canSelect() {
      return this.canApprove || this.canCancel;
    },
    bulkActions() {
      const actions = [];

      if (this.canApprove) {
        actions.push({
          key: "approve",
          label: "Aprovar vigências",
          icon: "check-circle",
          variant: "success",
          loading: this.bulkActionPending === "approve",
        });
      }

      if (this.canCancel) {
        actions.push({
          key: "cancel",
          label: "Cancelar vigências",
          icon: "minus-circle",
          variant: "danger",
          loading: this.bulkActionPending === "cancel",
        });
      }

      return actions;
    },
    selectedRecords() {
      return this.items.filter(
        (item) => this.selectedRowKeys.indexOf(item.id) !== -1,
      );
    },
  },
  created() {
    const fromQuery = readStatusFiltersFromQuery(this.$route.query);
    const queryChanged = Object.keys(fromQuery).some(
      (key) => fromQuery[key] !== this.filters[key],
    );
    const tableStatusFromQuery =
      fromQuery.tableStatus &&
      fromQuery.tableStatus !== FREIGHT_TABLE_STATUS_FILTER.ALL;

    this.activeFilters = {
      ...this.filters,
      ...fromQuery,
      isDraft: tableStatusFromQuery ? null : this.filters.isDraft,
      fornecedor: this.filters.fornecedor.slice(),
    };
    this.syncStatusQuery();

    this.fetchFreightTables({
      page: queryChanged ? 1 : this.pagination.current,
      tableStatus: this.activeFilters.tableStatus,
      effectivePeriodStatus: this.activeFilters.effectivePeriodStatus,
      isDraft: this.activeFilters.isDraft,
    }).finally(() => {
      this.hasFetched = true;
    });
  },
  beforeRouteLeave(to, from, next) {
    if (!isFreightTablePath(to.path)) {
      this.resetListState();
    }
    next();
  },
  methods: {
    ...mapActions("freightTables", [
      "fetchFreightTables",
      "exportFreightTableCsv",
      "approveTablesValidities",
      "cancelTablesValidities",
      "deleteFreightTableDraft",
      "resetListState",
      "importFreightTableRoutes",
      "downloadFreightTableRoutesTemplate",
    ]),
    onStatusTabChange(key) {
      const tab = FREIGHT_TABLE_TABS.filter((item) => item.key === key)[0];
      if (!tab || key === this.activeTab) return;

      this.selectedRowKeys = [];
      this.activeFilters = {
        ...this.activeFilters,
        tableStatus: tab.tableStatus,
        isDraft: tab.isDraft,
      };
      this.syncStatusQuery();
      this.dispatchFetch({
        page: 1,
        tableStatus: tab.tableStatus,
        isDraft: tab.isDraft,
      });
    },
    goToCreate() {
      this.$router.push("/tabela-frete/rascunho");
    },
    onHeaderAction(key) {
      if (key === "download-template") {
        this.onDownloadSpreadsheet();
        return;
      }

      if (key === "import-spreadsheet") {
        this.onImportRoutes();
      }
    },
    onImportRoutes() {
      if (this.importing) return;

      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".csv,.txt,text/csv";
      input.onchange = (event) => this.onImportFileSelected(event);
      input.click();
    },
    async onImportFileSelected(event) {
      const input = event && event.target ? event.target : null;
      const file = input && input.files ? input.files[0] : null;

      if (input) {
        input.value = "";
      }

      if (!file) return;

      if (!/\.(csv|txt)$/i.test(file.name || "")) {
        this.$message.warning("Selecione um arquivo .csv ou .txt para importar");
        return;
      }

      // Mesmo teto do backend (5 MB): sem isto o 413 cai na mensagem genérica e o operador
      // não descobre que o problema é o tamanho.
      if (file.size > IMPORT_MAX_BYTES) {
        this.$message.warning(
          "O arquivo passa de 5 MB. Divida a planilha e importe em partes.",
        );
        return;
      }

      this.importing = true;

      try {
        const data = await this.importFreightTableRoutes({ file });

        this.$notification.success({
          message: "Planilha importada",
          description:
            (data && data.message) || "As rotas da planilha foram importadas.",
        });
        // Só o dispatchFetch: os contadores das abas passam a vir no `meta` da própria
        // listagem, e `refreshCounts` deixa de existir. Chamá-lo aqui viraria referência
        // para método removido no primeiro merge.
        this.dispatchFetch({ page: 1 });
      } catch (error) {
        // O importador devolve 422 com [{linha, coluna, mensagem}] e o FormRequest
        // (extensão e tamanho) devolve 422 com {file: [...]}; qualquer outra coisa
        // (413, 500, rede) cai no tratamento padrão.
        const importError = normalizeImportError(error);

        if (importError) {
          this.importError = importError;
          this.importErrorsVisible = true;
        } else {
          handleApiError(this, error, "Não foi possível importar a planilha");
        }
      } finally {
        this.importing = false;
      }
    },
    async onDownloadSpreadsheet() {
      if (this.downloadingTemplate) return;

      this.downloadingTemplate = true;

      try {
        const data = await this.downloadFreightTableRoutesTemplate();

        downloadBlobFile(data, "TAB_FRE_MODELO.csv");
      } catch (error) {
        handleApiError(this, error, "Não foi possível baixar o modelo");
      } finally {
        this.downloadingTemplate = false;
      }
    },
    closeImportErrors() {
      this.importErrorsVisible = false;
    },
    onRetryImport() {
      this.importErrorsVisible = false;
      this.onImportRoutes();
    },
    onDownloadImportReport() {
      if (!this.importError) return;

      const report = buildImportErrorReport(this.importError.rows);

      downloadBlobFile(report.content, report.filename);
    },
    onFilterChange(filters) {
      this.activeFilters = { ...this.activeFilters, ...filters };
      this.selectedRowKeys = [];
      this.dispatchFetch({ search: filters.search, page: 1 });
    },
    onTableChange({ page, perPage, sort }) {
      this.selectedRowKeys = [];
      const sizeChanged = perPage !== this.pagination.perPage;
      const sortChanged = sort !== this.activeFilters.sort;
      this.activeFilters = { ...this.activeFilters, sort };
      this.dispatchFetch({
        page: sizeChanged || sortChanged ? 1 : page,
        perPage,
        sort,
      });
    },
    onPeriodChange({ dtInicio, dtFim }) {
      this.selectedRowKeys = [];
      this.activeFilters = { ...this.activeFilters, dtInicio, dtFim };
      this.dispatchFetch({ page: 1, dtInicio, dtFim });
    },
    onApplyFilters({ sort, tableStatus, effectivePeriodStatus, fornecedor }) {
      this.filtersModalVisible = false;
      this.selectedRowKeys = [];

      const isDraft =
        tableStatus !== FREIGHT_TABLE_STATUS_FILTER.ALL
          ? null
          : this.activeFilters.isDraft;
      this.activeFilters = {
        ...this.activeFilters,
        sort,
        tableStatus,
        effectivePeriodStatus,
        isDraft,
        fornecedor,
      };
      this.syncStatusQuery();
      this.dispatchFetch({
        page: 1,
        sort,
        tableStatus,
        effectivePeriodStatus,
        isDraft,
        fornecedor: (fornecedor || []).map((item) => item.key),
      });
    },
    onClearFilters() {
      this.filtersModalVisible = false;
      this.selectedRowKeys = [];
      this.activeFilters = {
        ...this.activeFilters,
        sort: null,
        tableStatus: FREIGHT_TABLE_STATUS_FILTER.ALL,
        effectivePeriodStatus: FREIGHT_VALIDITY_STATUS_FILTER.ALL,
        fornecedor: [],
        dtInicio: null,
        dtFim: null,
      };
      this.syncStatusQuery();
      this.dispatchFetch({
        page: 1,
        sort: null,
        tableStatus: FREIGHT_TABLE_STATUS_FILTER.ALL,
        effectivePeriodStatus: FREIGHT_VALIDITY_STATUS_FILTER.ALL,
        fornecedor: [],
        dtInicio: null,
        dtFim: null,
      });
    },
    // replace e não push: cada troca de filtro não vira uma entrada no histórico.
    syncStatusQuery() {
      const query = buildStatusFiltersQuery(
        this.$route.query,
        this.activeFilters,
      );
      if (isSameStatusQuery(query, this.$route.query)) return;

      this.$router.replace({ path: this.$route.path, query });
    },
    dispatchFetch(payload) {
      this.fetchFreightTables(payload).catch((error) => {
        handleApiError(
          this,
          error,
          "Não foi possível carregar as tabelas de frete",
        );
      });
    },
    onViewDetails(record) {
      this.$router.push(
        record.isDraft
          ? `/tabela-frete/rascunho/${record.freightTableId}`
          : `/tabela-frete/${record.freightTableId}`,
      );
    },
    onToggleAll(checked) {
      this.selectedRowKeys = checked ? this.items.map((item) => item.id) : [];
    },
    onBulkAction(key) {
      if (key === "approve") {
        this.confirmBulkApprove();
        return;
      }

      this.confirmBulkCancel();
    },
    confirmBulkApprove() {
      const records = this.selectedRecords;
      const pendentes = records.reduce(
        (total, record) => total + (record.vigenciasPendentes || 0),
        0,
      );

      if (!pendentes) {
        this.$notification.warning({
          message: "Nenhuma vigência pendente",
          description:
            "Apenas vigências pendentes são aprovadas, e nenhuma das tabelas selecionadas tem alguma.",
        });
        return;
      }

      Modal.confirm({
        title: "Aprovar vigências",
        icon: (h) =>
          h("a-icon", { props: { type: "info-circle", theme: "filled" } }),
        content: `Ao confirmar, você aprova ${this.validityLabel(
          pendentes,
          "pendente",
        )} ${this.tableLabel(records.length)}. Deseja continuar?`,
        okText: "Aprovar",
        cancelText: "Cancelar",
        class: "freight-tables-confirm",
        onOk: () => this.runBulkAction("approve", records),
      });
    },
    confirmBulkCancel() {
      const records = this.selectedRecords;
      const cancelaveis = records.reduce(
        (total, record) => total + (record.vigenciasCancelaveis || 0),
        0,
      );

      if (!cancelaveis) {
        this.$notification.warning({
          message: "Nenhuma vigência para cancelar",
          description:
            "Todas as vigências das tabelas selecionadas já estão canceladas.",
        });
        return;
      }

      Modal.confirm({
        title: "Cancelar vigências",
        icon: (h) =>
          h("a-icon", {
            props: { type: "exclamation-circle", theme: "filled" },
          }),
        content: `Ao confirmar, você cancela ${this.validityLabel(
          cancelaveis,
          "ainda não cancelada",
        )} ${this.tableLabel(
          records.length,
        )}. Essa ação não pode ser desfeita. Deseja continuar?`,
        okText: "Cancelar vigências",
        okType: "danger",
        cancelText: "Voltar",
        class: "freight-tables-confirm freight-tables-confirm--danger",
        onOk: () => this.runBulkAction("cancel", records),
      });
    },
    validityLabel(total, qualificador) {
      return total === 1
        ? `a vigência ${qualificador}`
        : `as ${total} vigências ${qualificador}s`;
    },
    tableLabel(total) {
      return total === 1
        ? "da tabela selecionada"
        : `das ${total} tabelas selecionadas`;
    },
    async runBulkAction(action, records) {
      const approve = action === "approve";
      this.bulkActionPending = action;

      try {
        const freightTableIds = records.map((record) => record.freightTableId);
        const result = approve
          ? await this.approveTablesValidities({ freightTableIds })
          : await this.cancelTablesValidities({ freightTableIds });

        this.selectedRowKeys = [];
        this.$notification.success({
          message: (result && result.title) || "Sucesso",
          description:
            (result && result.message) ||
            (approve
              ? "Vigência(s) aprovada(s) com sucesso!"
              : "Vigência(s) cancelada(s) com sucesso!"),
        });
      } catch (error) {
        handleApiError(
          this,
          error,
          approve
            ? "Não foi possível aprovar as vigências"
            : "Não foi possível cancelar as vigências",
        );
      } finally {
        this.bulkActionPending = null;
      }
    },
    onRowAction({ key, record }) {
      if (key === "discard-draft") {
        this.confirmDiscardDraft(record);
        return;
      }

      if (key === "add-carrier") {
        this.$router.push(addCarrierPath(record.freightTableId));
        return;
      }

      if (key === "add-route") {
        this.$router.push(addRoutePath(record.freightTableId));
        return;
      }

      if (key === "export-csv") {
        this.exportModal = {
          visible: true,
          loading: false,
          freightTableId: record.freightTableId,
          codigo: record.codigo,
        };
        return;
      }

      if (key === "approve-validity") {
        this.confirmApproveValidities(record);
        return;
      }

      if (key === "cancel-validity") {
        this.confirmCancelValidities(record);
      }
    },
    confirmDiscardDraft(record) {
      confirmDeleteDraft({
        descricao: record.descricao,
        codigo: record.codigo,
        onOk: () => this.discardDraft(record),
      });
    },
    async discardDraft(record) {
      try {
        await this.deleteFreightTableDraft({ tabfreId: record.freightTableId });
        this.$notification.success({
          message: "Rascunho descartado",
          description: "O rascunho foi removido da listagem.",
        });
        this.dispatchFetch({ page: this.pagination.current });
      } catch (error) {
        handleApiError(this, error, "Não foi possível descartar o rascunho");
      }
    },
    confirmApproveValidities(record) {
      if (!record.totalVigencias) {
        this.$notification.warning({
          message: "Nenhuma vigência para aprovar",
          description: "Esta tabela de frete não possui vigências cadastradas.",
        });
        return;
      }

      if (!record.vigenciasPendentes) {
        this.$notification.warning({
          message: "Nenhuma vigência pendente",
          description:
            "Apenas vigências pendentes são aprovadas, e esta tabela não tem nenhuma.",
        });
        return;
      }

      const nome = [record.descricao, record.codigo]
        .filter(Boolean)
        .join(" - ");
      const total = record.vigenciasPendentes;
      const pendentes =
        total === 1 ? "a vigência pendente" : `as ${total} vigências pendentes`;

      Modal.confirm({
        title: "Aprovar todas as vigências",
        icon: (h) =>
          h("a-icon", { props: { type: "info-circle", theme: "filled" } }),
        content: (h) =>
          h("span", [
            `Ao confirmar, você aprova ${pendentes} da tabela `,
            h("strong", nome),
            ". Deseja continuar?",
          ]),
        okText: "Aprovar",
        cancelText: "Cancelar",
        class: "freight-tables-confirm",
        onOk: () => this.approveValidities(record),
      });
    },
    confirmCancelValidities(record) {
      if (!record.totalVigencias) {
        this.$notification.warning({
          message: "Nenhuma vigência para cancelar",
          description: "Esta tabela de frete não possui vigências cadastradas.",
        });
        return;
      }

      if (!record.vigenciasCancelaveis) {
        this.$notification.warning({
          message: "Nenhuma vigência para cancelar",
          description: "Todas as vigências desta tabela já estão canceladas.",
        });
        return;
      }

      const nome = [record.descricao, record.codigo]
        .filter(Boolean)
        .join(" - ");
      const total = record.vigenciasCancelaveis;
      const alvo =
        total === 1
          ? "a vigência ainda não cancelada"
          : `as ${total} vigências ainda não canceladas`;

      Modal.confirm({
        title: "Cancelar todas as vigências",
        icon: (h) =>
          h("a-icon", {
            props: { type: "exclamation-circle", theme: "filled" },
          }),
        content: (h) =>
          h("span", [
            `Ao confirmar, você cancela ${alvo} da tabela `,
            h("strong", nome),
            ". Essa ação não pode ser desfeita. Deseja continuar?",
          ]),
        okText: "Cancelar vigências",
        okType: "danger",
        cancelText: "Voltar",
        class: "freight-tables-confirm freight-tables-confirm--danger",
        onOk: () => this.cancelValidities(record),
      });
    },
    async cancelValidities(record) {
      try {
        const result = await this.cancelTablesValidities({
          freightTableIds: [record.freightTableId],
        });
        this.$notification.success({
          message: (result && result.title) || "Sucesso",
          description:
            (result && result.message) ||
            "Vigência(s) cancelada(s) com sucesso!",
        });
      } catch (error) {
        handleApiError(this, error, "Não foi possível cancelar as vigências");
      }
    },
    async approveValidities(record) {
      try {
        const result = await this.approveTablesValidities({
          freightTableIds: [record.freightTableId],
        });
        this.$notification.success({
          message: (result && result.title) || "Sucesso",
          description:
            (result && result.message) ||
            "Vigência(s) aprovada(s) com sucesso!",
        });
      } catch (error) {
        handleApiError(this, error, "Não foi possível aprovar as vigências");
      }
    },
    closeExportModal() {
      this.exportModal = {
        visible: false,
        loading: false,
        freightTableId: null,
        codigo: null,
      };
    },
    async onConfirmExport({ onlyActive }) {
      this.exportModal = { ...this.exportModal, loading: true };

      try {
        const data = await this.exportFreightTableCsv({
          freightTableId: this.exportModal.freightTableId,
          onlyActive,
        });
        downloadBlobFile(data, `TAB_FRE_${this.exportModal.codigo}.csv`);
        this.closeExportModal();
      } catch (error) {
        this.exportModal = { ...this.exportModal, loading: false };
        handleApiError(
          this,
          error,
          "Não foi possível exportar a tabela de frete",
        );
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-tables-view {
  font-family: @font-primary;
  padding: 24px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  &__title {
    // font-family explícita: o tema global força Montserrat em h1..h6
    font-family: @font-primary;
    font-size: 20px;
    font-weight: 600;
    color: @color-text-dark;
    margin: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__create-btn {
    background-color: @color-primary;
    border-color: @color-primary;
    border-radius: 6px;
    height: 36px;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }

  &__no-results {
    padding: 48px;
  }
}
</style>
